import { Component, type ComponentChildren } from "preact";
import { reportError } from "./lib/errors";

interface Props {
  children: ComponentChildren;
}

interface State {
  crashed: boolean;
}

/**
 * Top-level error boundary (issue #18). A render/lifecycle crash anywhere in the
 * funnel is caught here, reported to GA4 as a fatal `exception` (PII-safe — see
 * lib/errors), and replaced with a fallback that keeps the human-contact path
 * open so a crash never silently loses a lead. Works in embed mode too, where
 * the site footer (and its phone numbers) isn't rendered.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { crashed: false };

  static getDerivedStateFromError(): State {
    return { crashed: true };
  }

  componentDidCatch(error: unknown): void {
    reportError(error, { source: "error-boundary", fatal: true });
  }

  render() {
    if (!this.state.crashed) return this.props.children;

    return (
      <div class="error-fallback" role="alert">
        <h1>Something went wrong</h1>
        <p>
          We hit an unexpected problem on our end. Your details weren&rsquo;t lost — reach a
          Kasselman Solar generator specialist and we&rsquo;ll pick up right where you left off.
        </p>
        <p class="error-fallback-contact">
          <a href="tel:+15184788365">Upstate (518) 478-8365</a>
          <span class="sep" aria-hidden="true">·</span>
          <a href="tel:+18456814088">Hudson Valley (845) 681-4088</a>
          <span class="sep" aria-hidden="true">·</span>
          <a href="mailto:info@kasselmansolar.com">info@kasselmansolar.com</a>
        </p>
        <button type="button" class="primary" onClick={() => window.location.reload()}>
          Start over
        </button>
      </div>
    );
  }
}
